import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import Input from "./ui/input";
import Button from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "./ui/use-toast";
import useClienteEmbarcacionesHistorial from "../hooks/useClienteEmbarcacionesHistorial";

const FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "vinculadas", label: "Vinculadas" },
  { value: "desvinculadas", label: "Desvinculadas" },
  { value: "activas", label: "Activas" },
  { value: "inactivas", label: "Inactivas" },
];

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const parsedDate = new Date(dateString);
  if (isNaN(parsedDate)) return "—";
  return parsedDate.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const resolveActivo = (registro) => {
  if (typeof registro.activo === "boolean") {
    return registro.activo;
  }
  return !registro.hasta;
};

const formatDuration = (desde, hasta) => {
  if (!desde || !hasta) return "—";
  const start = new Date(desde);
  const end = new Date(hasta);
  if (isNaN(start) || isNaN(end)) return "—";
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "—";
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0 && hours > 0) {
    return `${days}d ${hours}h`;
  }
  if (days > 0) {
    return `${days}d`;
  }
  return `${hours}h`;
};

const HistorialEmbarcacionesModal = ({ open, onClose, clienteId }) => {
  const { historial, loading, error } = useClienteEmbarcacionesHistorial(
    clienteId,
    open
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("todas");

  useEffect(() => {
    if (!open) return;
    setSearchTerm("");
    setFilter("todas");
  }, [open]);

  useEffect(() => {
    if (!error) return;
    toast({
      title: "Error",
      description: "No se pudo cargar el historial de embarcaciones.",
      variant: "destructive",
    });
  }, [error]);

  const filteredHistorial = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return (historial || [])
      .slice()
      .sort((a, b) => new Date(b.desde) - new Date(a.desde))
      .filter((registro) => {
        const matricula = registro.numeroMatricula || "";
        const nombre = registro.nombreFantasia || "";
        const matchesSearch =
          !normalizedSearch ||
          matricula.toLowerCase().includes(normalizedSearch) ||
          nombre.toLowerCase().includes(normalizedSearch);

        const isActive = resolveActivo(registro);
        const matchesFilter = (() => {
          switch (filter) {
            case "vinculadas":
              return registro.hasta == null;
            case "desvinculadas":
              return registro.hasta != null;
            case "activas":
              return isActive;
            case "inactivas":
              return !isActive;
            default:
              return true;
          }
        })();

        return matchesSearch && matchesFilter;
      });
  }, [historial, searchTerm, filter]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader className="flex items-center justify-between gap-4">
          <DialogTitle>Historial de embarcaciones</DialogTitle>
          <Button
            variant="ghost"
            className="text-azul-oscuro"
            onClick={onClose}
            aria-label="Cerrar historial"
          >
            <X className="h-5 w-5" />
          </Button>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            type="text"
            placeholder="Buscar por matrícula o nombre"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={filter === option.value ? "secondary" : "outline"}
                className="text-sm"
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="rounded-lg border border-azul-oscuro/10 bg-azul-lago/5 p-6 text-center text-azul-oscuro">
              Cargando historial...
            </div>
          ) : filteredHistorial.length === 0 ? (
            <div className="rounded-lg border border-azul-oscuro/10 bg-azul-lago/5 p-6 text-center text-azul-oscuro">
              No hay historial de embarcaciones para mostrar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matrícula</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Vinculada desde</TableHead>
                    <TableHead>Desvinculada el</TableHead>
                    <TableHead>Duración</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHistorial.map((registro) => {
                    const isActive = resolveActivo(registro);
                    return (
                      <TableRow key={`${registro.embarcacion_id}-${registro.desde}`}>
                        <TableCell>{registro.numeroMatricula || "—"}</TableCell>
                        <TableCell>{registro.nombreFantasia || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={isActive ? "default" : "secondary"}>
                            {isActive ? "Activa" : "Desvinculada"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(registro.desde)}</TableCell>
                        <TableCell>{formatDate(registro.hasta)}</TableCell>
                        <TableCell>
                          {formatDuration(registro.desde, registro.hasta)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistorialEmbarcacionesModal;
